import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import type { BudgetStateData } from '../components/reviews/CostBanner';
import { POOLS, type CostPool } from '../lib/pools';

interface ReviewServiceConfig {
  enabled: boolean;
  reason?: string;
  disabledBy?: string;
  disabledAt?: string;
}

/**
 * ReviewSettings — edit daily/monthly budget limits, manually reset counters,
 * or force-reopen the circuit breaker. Spec 188 Phase 5.
 * Review service toggle added in Spec 206 Phase 4.
 */
export function ReviewSettings() {
  const navigate = useNavigate();
  const [selectedPool, setSelectedPool] = useState<CostPool>('review');
  const [budget, setBudget] = useState<BudgetStateData | null>(null);
  const [dailyLimit, setDailyLimit] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeAction, setActiveAction] = useState<
    'saveLimits' | 'resetDaily' | 'resetMonthly' | 'override' | null
  >(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Review service toggle state (Spec 206)
  const [serviceConfig, setServiceConfig] = useState<ReviewServiceConfig | null>(null);
  const [serviceLoading, setServiceLoading] = useState(true);
  const [disableReason, setDisableReason] = useState('');
  const [togglingService, setTogglingService] = useState(false);

  // Re-fetch budget whenever the selected pool changes (Spec 199 Phase 5
  // — per-pool ReviewSettings: each pool has independent limits/state).
  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      // Clear the previous pool's input + status state immediately so the
      // form doesn't briefly show stale values while the new pool's fetch
      // is in flight.
      setBudget(null);
      setDailyLimit('');
      setMonthlyLimit('');
      setOverrideReason('');
      setMessage(null);
      try {
        const data = await api.budget.get(selectedPool);
        if (!active) return;
        setBudget(data);
        setDailyLimit(String(data.dailyLimitUsd));
        setMonthlyLimit(String(data.monthlyLimitUsd));
      } catch (err) {
        if (!active) return;
        setMessage({ type: 'error', text: `Failed to load budget: ${err instanceof Error ? err.message : String(err)}` });
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [selectedPool]);

  // Load review service config
  useEffect(() => {
    let active = true;
    async function loadConfig() {
      try {
        const config = await api.reviews.getConfig();
        if (!active) return;
        setServiceConfig(config);
      } catch (err) {
        if (!active) return;
        setMessage({ type: 'error', text: `Failed to load service config: ${err instanceof Error ? err.message : String(err)}` });
      } finally {
        if (active) setServiceLoading(false);
      }
    }
    loadConfig();
    return () => {
      active = false;
    };
  }, []);

  async function toggleService() {
    if (!serviceConfig) return;
    const newEnabled = !serviceConfig.enabled;

    // When disabling, require a reason
    if (!newEnabled && !disableReason.trim()) {
      setMessage({ type: 'error', text: 'A reason is required when disabling the review service' });
      return;
    }

    // Optimistic update
    const previousConfig = serviceConfig;
    setServiceConfig({ ...serviceConfig, enabled: newEnabled });
    setTogglingService(true);

    try {
      const updated = await api.reviews.setConfig({
        enabled: newEnabled,
        reason: newEnabled ? undefined : disableReason.trim(),
      });
      setServiceConfig(updated);
      setDisableReason('');
      setMessage({ type: 'success', text: newEnabled ? 'Review service enabled' : 'Review service disabled' });
    } catch (err) {
      // Revert on error
      setServiceConfig(previousConfig);
      setMessage({ type: 'error', text: `Toggle failed: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setTogglingService(false);
    }
  }

  async function saveLimits() {
    const daily = Number(dailyLimit);
    const monthly = Number(monthlyLimit);
    if (!Number.isFinite(daily) || daily <= 0 || !Number.isFinite(monthly) || monthly <= 0) {
      setMessage({ type: 'error', text: 'Limits must be positive finite numbers' });
      return;
    }
    setSaving(true);
    setActiveAction('saveLimits');
    try {
      const updated = await api.budget.updateLimits(
        { dailyLimitUsd: daily, monthlyLimitUsd: monthly },
        selectedPool
      );
      setBudget(updated);
      setMessage({ type: 'success', text: `Limits saved for ${selectedPool} pool` });
    } catch (err) {
      setMessage({ type: 'error', text: `Save failed: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setSaving(false);
      setActiveAction(null);
    }
  }

  async function doReset(scope: 'daily' | 'monthly') {
    const confirmed = window.confirm(`Reset ${scope} counter to zero?`);
    if (!confirmed) return;
    setSaving(true);
    setActiveAction(scope === 'daily' ? 'resetDaily' : 'resetMonthly');
    try {
      const updated = await api.budget.reset(scope, selectedPool);
      setBudget(updated);
      setMessage({ type: 'success', text: `${scope} counter reset for ${selectedPool} pool` });
    } catch (err) {
      setMessage({ type: 'error', text: `Reset failed: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setSaving(false);
      setActiveAction(null);
    }
  }

  async function doOverride() {
    if (!overrideReason.trim()) {
      setMessage({ type: 'error', text: 'Override reason is required' });
      return;
    }
    setSaving(true);
    setActiveAction('override');
    try {
      const updated = await api.budget.override(overrideReason.trim(), selectedPool);
      setBudget(updated);
      setOverrideReason('');
      setMessage({ type: 'success', text: `Circuit breaker reopened for ${selectedPool} pool` });
    } catch (err) {
      setMessage({ type: 'error', text: `Override failed: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setSaving(false);
      setActiveAction(null);
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/reviews')}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
            aria-label="Back to reviews"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold">Review Budget Settings</h1>
        </div>

        {loading && <div data-testid="settings-loading">Loading…</div>}

        {message && (
          <div
            data-testid="settings-message"
            data-kind={message.type}
            className={`mb-4 rounded-lg border px-4 py-2 ${
              message.type === 'success'
                ? 'border-green-700 bg-green-900/30 text-green-300'
                : 'border-red-700 bg-red-900/30 text-red-300'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Review Service Toggle (Spec 206) */}
        {!serviceLoading && serviceConfig && (
          <section className="mb-6 space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-4" data-testid="service-toggle-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Review Service</h2>
                <p className="text-sm text-gray-400">
                  {serviceConfig.enabled
                    ? 'Enabled — reviews are processed automatically'
                    : `Disabled${serviceConfig.disabledAt ? ` since ${new Date(serviceConfig.disabledAt).toLocaleString()}` : ''}`}
                </p>
                {!serviceConfig.enabled && serviceConfig.reason && (
                  <p className="mt-1 text-sm text-yellow-400" data-testid="service-disabled-reason">
                    Reason: {serviceConfig.reason}
                  </p>
                )}
              </div>
              <button
                role="switch"
                aria-checked={serviceConfig.enabled}
                aria-label="Toggle review service"
                onClick={toggleService}
                disabled={togglingService || (serviceConfig.enabled && !disableReason.trim())}
                data-testid="service-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  serviceConfig.enabled ? 'bg-teal-500' : 'bg-gray-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  serviceConfig.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {serviceConfig.enabled && (
              <div>
                <label className="block">
                  <span className="mb-1 block text-sm text-gray-400">Reason for disabling (required to turn off)</span>
                  <input
                    type="text"
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.target.value)}
                    data-testid="input-disable-reason"
                    placeholder="e.g. Investigating false positives"
                    className="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2"
                  />
                </label>
              </div>
            )}
          </section>
        )}
        {serviceLoading && <div data-testid="service-loading" className="mb-6 text-gray-400">Loading service config…</div>}

        {/* Per-pool tab selector (Spec 199 Phase 5) — review/agent/cli each
            have independent BudgetDO state, limits, and circuit breaker. */}
        <section
          className="mb-6 rounded-lg border border-gray-800 bg-gray-900 p-2"
          data-testid="pool-tabs"
        >
          <div className="flex gap-1" role="tablist" aria-label="Cost pool">
            {POOLS.map((pool) => (
              <button
                key={pool}
                type="button"
                role="tab"
                aria-selected={selectedPool === pool}
                data-testid={`pool-tab-${pool}`}
                onClick={() => setSelectedPool(pool)}
                disabled={saving}
                className={`flex-1 rounded px-3 py-2 text-sm font-medium uppercase transition-colors disabled:opacity-50 ${
                  selectedPool === pool
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {pool}
              </button>
            ))}
          </div>
        </section>

        {budget && !loading && (
          <>
            <section className="mb-6 space-y-2 rounded-lg border border-gray-800 bg-gray-900 p-4">
              <h2 className="text-lg font-semibold">Current state ({selectedPool} pool)</h2>
              <p data-testid="current-daily">Daily: ${budget.dailySpentUsd.toFixed(2)} / ${budget.dailyLimitUsd.toFixed(2)}</p>
              <p data-testid="current-monthly">Monthly: ${budget.monthlySpentUsd.toFixed(2)} / ${budget.monthlyLimitUsd.toFixed(2)}</p>
              <p data-testid="current-circuit">Circuit: {budget.circuitOpen ? 'OPEN' : 'closed'}</p>
            </section>

            <section className="mb-6 space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-4">
              <h2 className="text-lg font-semibold">Limits</h2>
              <label className="block">
                <span className="mb-1 block text-sm text-gray-400">Daily limit (USD)</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(e.target.value)}
                  data-testid="input-daily-limit"
                  className="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm text-gray-400">Monthly limit (USD)</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={monthlyLimit}
                  onChange={(e) => setMonthlyLimit(e.target.value)}
                  data-testid="input-monthly-limit"
                  className="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2"
                />
              </label>
              <button
                data-testid="save-limits"
                onClick={saveLimits}
                disabled={saving}
                className="rounded bg-teal-600 px-4 py-2 font-medium hover:bg-teal-500 disabled:opacity-50"
              >
                {activeAction === 'saveLimits' ? 'Saving…' : 'Save limits'}
              </button>
            </section>

            <section className="mb-6 space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-4">
              <h2 className="text-lg font-semibold">Reset counters</h2>
              <div className="flex gap-2">
                <button
                  data-testid="reset-daily"
                  onClick={() => doReset('daily')}
                  disabled={saving}
                  className="rounded bg-gray-700 px-4 py-2 hover:bg-gray-600 disabled:opacity-50"
                >
                  {activeAction === 'resetDaily' ? 'Resetting…' : 'Reset daily'}
                </button>
                <button
                  data-testid="reset-monthly"
                  onClick={() => doReset('monthly')}
                  disabled={saving}
                  className="rounded bg-gray-700 px-4 py-2 hover:bg-gray-600 disabled:opacity-50"
                >
                  {activeAction === 'resetMonthly' ? 'Resetting…' : 'Reset monthly'}
                </button>
              </div>
            </section>

            <section className="space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-4">
              <h2 className="text-lg font-semibold">Force reopen circuit</h2>
              <label className="block">
                <span className="mb-1 block text-sm text-gray-400">Override reason</span>
                <input
                  type="text"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  data-testid="input-override-reason"
                  placeholder="e.g. Emergency deploy retry"
                  className="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2"
                />
              </label>
              <button
                data-testid="override-circuit"
                onClick={doOverride}
                disabled={saving}
                className="rounded bg-red-700 px-4 py-2 font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {activeAction === 'override' ? 'Reopening…' : 'Reopen circuit'}
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
